import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { createPost, getCategories } from '../../../models/postModel';
import { toast } from 'react-toastify';
import WysiwygEditor from '../../../components/common/WysiwygEditor';

function PostCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    post_title: '',
    post_text: '',
    category_id: '',
    post_status: 'published'
  });

  useEffect(() => {
    async function fetchCategories() {
      try {
        console.log('Fetching categories...');
        const response = await getCategories();
        console.log('Categories response:', response);
        if (response.status === 1) {
          setCategories(response.categories || []);
          console.log('Categories set:', response.categories);
        } else {
          console.error('Categories API error:', response.message);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to default categories
        setCategories([
          { category_id: 1, category_name: 'General' },
          { category_id: 2, category_name: 'News' },
          { category_id: 3, category_name: 'Updates' },
          { category_id: 4, category_name: 'Announcements' }
        ]);
      }
    }
    fetchCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('handleSubmit called');
    console.log('formData:', formData);
    
    if (!formData.post_title.trim()) {
      toast.error('Post title is required');
      return;
    }

    if (!formData.post_text.trim()) {
      toast.error('Post content is required');
      return;
    }

    try {
      console.log('About to call createPost with:', formData);
      setLoading(true);
      const response = await createPost(formData);
      console.log('createPost response:', response);
      
      if (response.status === 1) {
        toast.success('Post created successfully');
        navigate('..', { state: { fromForm: true } });
      } else {
        throw new Error(response.message || 'Failed to create post');
      }
    } catch (err) {
      console.error('Error creating post:', err);
      toast.error(err.message || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Create New Post</h1>
      </div>

      <form onSubmit={(e) => {
        console.log('Form onSubmit triggered');
        handleSubmit(e);
      }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Post Details */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Post Details</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="category_id" className="block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <select
                    id="category_id"
                    name="category_id"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                    value={formData.category_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category.category_id} value={category.category_id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="post_status" className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    id="post_status"
                    name="post_status"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                    value={formData.post_status}
                    onChange={handleInputChange}
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="flex flex-col gap-3">
                <button 
                  type="submit" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0087D4] hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : 'Create Post'}
                </button>
                <button 
                  type="button" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Post Content */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Post Content</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="post_title" className="block text-sm font-medium text-gray-700">
                    Post Title *
                  </label>
                  <input
                    type="text"
                    id="post_title"
                    name="post_title"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                    value={formData.post_title}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter post title"
                  />
                </div>

                <div>
                  <label htmlFor="post_text" className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <div className="border border-gray-300 rounded-md">
                    <WysiwygEditor
                      value={formData.post_text}
                      onChange={(content) => setFormData(prev => ({
                        ...prev,
                        post_text: content
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default PostCreate; 